import { sessionStorageCache } from 'services/cache';
import { getCurrentAsaasIdentityKey } from './identity';
import { isValidAsaasReturnId } from './returnHandoff';
import {
  ASAAS_CURRENCY,
  ASAAS_PENDING_TTL_MS,
  type AsaasOrderInfo,
  type AsaasPendingPayment,
  type AsaasPaymentSource,
} from './types';

interface PendingBucket {
  returns: Record<string, AsaasPendingPayment>;
}

type PendingStore = Record<string, PendingBucket>;

const pendingCache = sessionStorageCache.entry('@fett/asaas-pending-payments');
const SOURCES: AsaasPaymentSource[] = [
  'purchase',
  'upgrade-renew',
  'traffic',
  'lightweight',
  'billing',
];

function isPendingPayment(value: unknown): value is AsaasPendingPayment {
  if (!value || typeof value !== 'object') return false;
  const pending = value as AsaasPendingPayment;
  return (
    typeof pending.orderCode === 'string' &&
    pending.orderCode.length > 0 &&
    pending.orderCode.length <= 128 &&
    Number.isFinite(pending.createdAt) &&
    SOURCES.includes(pending.source) &&
    !!pending.order &&
    pending.order.orderCode === pending.orderCode &&
    pending.order.currency === ASAAS_CURRENCY &&
    typeof pending.order.discountTotalPrice === 'string'
  );
}

function readStore(): PendingStore {
  const value = pendingCache.get<unknown>();
  if (!value || typeof value !== 'object') return {};
  const store: PendingStore = {};
  for (const [identity, bucket] of Object.entries(value as Record<string, unknown>)) {
    if (!bucket || typeof bucket !== 'object') continue;
    const returns = (bucket as PendingBucket).returns;
    if (!returns || typeof returns !== 'object') continue;
    const validReturns: Record<string, AsaasPendingPayment> = {};
    for (const [returnId, pending] of Object.entries(returns)) {
      if (isValidAsaasReturnId(returnId) && isPendingPayment(pending)) {
        validReturns[returnId] = pending;
      }
    }
    if (Object.keys(validReturns).length) store[identity] = { returns: validReturns };
  }
  return store;
}

function writeStore(store: PendingStore): void {
  pendingCache.set(store);
}

function toSnapshot(order: AsaasOrderInfo): AsaasOrderInfo {
  return {
    orderCode: order.orderCode,
    payType: 'asaas',
    currency: ASAAS_CURRENCY,
    packageId: order.packageId,
    orderAction: order.orderAction,
    discountTotalPrice: order.discountTotalPrice,
    accountNum: order.accountNum,
    durationNum: order.durationNum,
    regionPackageId: order.regionPackageId,
    validStartStr: order.validStartStr,
    validEndStr: order.validEndStr,
    handleNum: order.handleNum,
    trafficPackageType: order.trafficPackageType,
    orderNumber: order.orderNumber,
  };
}

export function savePendingAsaasPayment(
  order: AsaasOrderInfo,
  source: AsaasPaymentSource,
  returnId: string,
): { ok: true } | { ok: false; reason: string } {
  const identity = getCurrentAsaasIdentityKey();
  if (!identity) return { ok: false, reason: 'no-identity' };
  if (!isValidAsaasReturnId(returnId)) return { ok: false, reason: 'invalid-return-id' };
  if (!order.orderCode || order.currency !== ASAAS_CURRENCY) {
    return { ok: false, reason: 'invalid-order' };
  }
  try {
    const store = readStore();
    const bucket = store[identity] ?? { returns: {} };
    const pending: AsaasPendingPayment = {
      orderCode: order.orderCode,
      createdAt: Date.now(),
      source,
      order: toSnapshot(order),
    };
    writeStore({
      ...store,
      [identity]: { returns: { ...bucket.returns, [returnId]: pending } },
    });
    const confirmed = readPendingAsaasPayment(returnId);
    return confirmed?.createdAt === pending.createdAt && confirmed.orderCode === order.orderCode
      ? { ok: true }
      : { ok: false, reason: 'save-failed' };
  } catch {
    return { ok: false, reason: 'save-failed' };
  }
}

export function readPendingAsaasPayment(returnId?: string): AsaasPendingPayment | undefined {
  const identity = getCurrentAsaasIdentityKey();
  if (!identity || !returnId || !isValidAsaasReturnId(returnId)) return undefined;
  const bucket = readStore()[identity];
  if (!bucket || !Object.prototype.hasOwnProperty.call(bucket.returns, returnId)) return undefined;
  return bucket.returns[returnId];
}

export function findPendingAsaasPaymentByOrderCode(
  orderCode: string,
): AsaasPendingPayment | undefined {
  const identity = getCurrentAsaasIdentityKey();
  if (!identity) return undefined;
  return Object.values(readStore()[identity]?.returns ?? {}).find(
    (pending) => pending.orderCode === orderCode,
  );
}

export function isAsaasPendingPaymentExpired(
  pending: AsaasPendingPayment,
  now = Date.now(),
): boolean {
  return now - pending.createdAt >= ASAAS_PENDING_TTL_MS;
}

export function clearPendingAsaasPayment(orderCode?: string): void {
  const identity = getCurrentAsaasIdentityKey();
  if (!identity) return;
  try {
    const store = readStore();
    const bucket = store[identity];
    if (!bucket) return;
    const returns = Object.fromEntries(
      Object.entries(bucket.returns).filter(
        ([, pending]) => orderCode !== undefined && pending.orderCode !== orderCode,
      ),
    );
    if (Object.keys(returns).length) store[identity] = { returns };
    else delete store[identity];
    writeStore(store);
  } catch {
    return;
  }
}

export type AsaasReturnRestore =
  | { outcome: 'inactive' }
  | { outcome: 'restored'; pending: AsaasPendingPayment }
  | { outcome: 'unrecoverable'; reason: 'snapshot-missing' | 'snapshot-expired' };

export function resolveAsaasReturnRestore(
  query: { returnId?: string } | undefined,
): AsaasReturnRestore {
  if (!query) return { outcome: 'inactive' };
  const pending = readPendingAsaasPayment(query.returnId);
  if (!pending) return { outcome: 'unrecoverable', reason: 'snapshot-missing' };
  if (isAsaasPendingPaymentExpired(pending)) {
    return { outcome: 'unrecoverable', reason: 'snapshot-expired' };
  }
  return { outcome: 'restored', pending };
}
